<template>
    <SettingsCard title="Показатели сотрудника">
        <div v-if="settingsStore.employeeStatisticsSettingsLoading" class="ds-settings-loading">
            <Loader />
        </div>
        <div v-else-if="loadError" class="ds-settings-state ds-settings-state-error">
            <p>Не удалось загрузить показатели сотрудника.</p>
            <btn @click="emit('retry-load')">Повторить</btn>
        </div>
        <template v-else-if="settingsStore.employeeStatisticsSettings">
            <div class="ds-settings-permission-grid">
                <div class="flex-1">
                    <RoleCheck
                        v-for="field in firstColumnFields"
                        :key="field.key"
                        v-model="values[field.key]"
                        :label="`${field.abbreviation} — ${field.label}`"
                        name=""
                    />
                </div>
                <div class="flex-1">
                    <RoleCheck
                        v-for="field in secondColumnFields"
                        :key="field.key"
                        v-model="values[field.key]"
                        :label="`${field.abbreviation} — ${field.label}`"
                        name=""
                    />
                </div>
            </div>
            <div class="ds-settings-actions">
                <btn @click="save" :disabled="!isChanged || saving" :class="{ 'opacity-50': !isChanged || saving }">
                    Сохранить
                </btn>
            </div>
            <div v-if="saveError" class="ds-settings-error" role="alert">
                Не удалось сохранить настройки. <button type="button" @click="save">Повторить</button>
            </div>
        </template>
    </SettingsCard>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import RoleCheck from '@/components/roles/RoleCheck.vue'
import SettingsCard from '@/ui/SettingsCard.vue'
import Loader from '@/ui/Loader.vue'
import { useSettingsStore } from '@/stores/settingsStore'
import type { EmployeeStatisticsSettings, EmployeeStatisticsSettingsPatch } from '@/types'

defineProps<{
    loadError: boolean
}>()

const emit = defineEmits<{
    (event: 'retry-load'): void
}>()

const settingsStore = useSettingsStore()
const values = reactive<EmployeeStatisticsSettings>({
    show_working_days: false,
    show_plan_hours: false,
    show_fact_hours: false,
    show_additional_plan: false,
    show_additional_fact: false,
    show_exchange_hours: false,
    show_hours_norm: false,
    show_overtime_from_norm: false,
    show_all_department_plan_hours: false,
    show_all_department_fact_hours: false
})
const saveError = ref(false)
const saving = ref(false)

const statisticFields = [
    { key: 'show_working_days', abbreviation: 'РД', label: 'Рабочие дни' },
    { key: 'show_plan_hours', abbreviation: 'ПЧ', label: 'План часов' },
    { key: 'show_fact_hours', abbreviation: 'ФЧ', label: 'Факт часов' },
    { key: 'show_additional_plan', abbreviation: 'ПД', label: 'План доп' },
    { key: 'show_additional_fact', abbreviation: 'ФД', label: 'Факт доп' },
    { key: 'show_exchange_hours', abbreviation: 'БСЧ', label: 'Часы биржи смен' },
    { key: 'show_hours_norm', abbreviation: 'НЧ', label: 'Норма часов' },
    { key: 'show_overtime_from_norm', abbreviation: 'ПН', label: 'Переработка от нормы' },
    { key: 'show_all_department_plan_hours', abbreviation: 'ПВ', label: 'План всего' },
    { key: 'show_all_department_fact_hours', abbreviation: 'ФВ', label: 'Факт всего' }
] as const satisfies ReadonlyArray<{
    key: keyof EmployeeStatisticsSettings
    abbreviation: string
    label: string
}>

watch(
    () => settingsStore.employeeStatisticsSettings,
    (settings) => {
        if (settings) Object.assign(values, settings)
    },
    { immediate: true }
)

const changedSettings = computed<EmployeeStatisticsSettingsPatch>(() => {
    const settings = settingsStore.employeeStatisticsSettings
    if (!settings) return {}

    return statisticFields.reduce<EmployeeStatisticsSettingsPatch>((changes, field) => {
        if (values[field.key] !== settings[field.key]) changes[field.key] = values[field.key]
        return changes
    }, {})
})

const isChanged = computed(() => Object.keys(changedSettings.value).length > 0)
const firstColumnFields = statisticFields.slice(0, 5)
const secondColumnFields = statisticFields.slice(5)

async function save() {
    if (!isChanged.value || saving.value) return

    saveError.value = false
    saving.value = true
    const error = await settingsStore.changeEmployeeStatisticsSettings(changedSettings.value)
    saving.value = false
    saveError.value = Boolean(error)
}
</script>
